import { useState } from "react";
import Dialog from "./Dialog";
import { useToast } from "../providers/ToastProvider";
import api from "../../services/api";

interface FulfillmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    id: string;
    type: string;
    taskType: "LINK" | "IMAGE" | "TEXT";
    taskTitle: string;
    studentName: string;
  } | null;
  onSuccess: () => void;
}

export default function FulfillmentDialog({ isOpen, onClose, item, onSuccess }: FulfillmentDialogProps) {
  const [proofUrl, setProofUrl] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const isValid = item ? (
    item.taskType === "LINK" ? proofUrl.trim().length > 0 :
    item.taskType === "IMAGE" ? notes.trim().length > 0 :
    item.taskType === "TEXT" ? notes.trim().length > 0 :
    true
  ) : false;

  const handleSubmit = async () => {
    if (!item || !isValid) return;
    setIsSubmitting(true);
    try {
      const newStatus = item.type === "EXPERT_JOB" ? "COMPLETED" : "RESOLVED";
      await api.patch(`/service-pool/${item.id}`, {
        status: newStatus, 
        type: item.type,
        contentUrl: proofUrl,
        evidenceUrl: evidenceUrl,
        feedback: notes 
      });

      showToast(
        "Requirement Filled", 
        `Mastery recorded successfully for ${item.studentName}`, 
        "success"
      );

      onSuccess();
      onClose();
      setProofUrl("");
      setEvidenceUrl("");
      setNotes("");
    } catch (err: any) {
      console.error("Resolution failed:", err);
      showToast(
        "Fulfillment Error",
        err.message || "Failed to finalize this requirement. Please try again.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Resolution Protocol"
      footer={
        <button 
          onClick={handleSubmit}
          disabled={isSubmitting || !isValid}
          className={`w-full py-4 sm:py-5 rounded-xl sm:rounded-[1.75rem] font-black text-xs uppercase tracking-[0.2em] shadow-2xl transition-all flex items-center justify-center gap-3 ${
            isValid
              ? "bg-on-surface text-surface hover:translate-y-[-4px] active:scale-[0.98]" 
              : "bg-surface-container-highest text-on-surface-variant/40 cursor-not-allowed opacity-50 border border-outline-variant/10"
          }`}
        >
          {isSubmitting ? (
            <span className="w-4 h-4 border-2 border-surface/20 border-t-surface rounded-full animate-spin" />
          ) : (
            <>
              <span className="material-symbols-outlined text-sm">{isValid ? "rocket_launch" : "lock"}</span>
              {isValid ? "Fulfill Requirement" : "Requirement Missing"}
            </>
          )}
        </button>
      }
    >
      <div className="space-y-6">
        <div className="space-y-5">
          {/* Image Upload Area Hidden */}

          {/* Link Area */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest px-1 flex justify-between">
              <span className={item?.taskType === "LINK" ? "text-primary" : "text-on-surface-variant/60"}>Deliverable Link</span>
              <span className={`text-[9px] font-bold ${item?.taskType === "LINK" ? "text-secondary" : "text-on-surface-variant/40"}`}>
                {item?.taskType === "LINK" ? "Required" : "Optional"}
              </span>
            </label>
            <div className="relative group">
              <span className={`material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-sm ${item?.taskType === "LINK" ? "text-primary" : "text-slate-400"}`}>link</span>
              <input 
                type="url"
                placeholder="https://..."
                className="w-full pl-12 pr-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium"
                value={proofUrl}
                onChange={(e) => setProofUrl(e.target.value)}
              />
            </div>
          </div>

          {/* Text Area */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest px-1 flex justify-between">
              <span className={item?.taskType === "TEXT" || item?.taskType === "IMAGE" ? "text-primary" : "text-on-surface-variant/60"}>Resolution Summary</span>
              <span className={`text-[9px] font-bold ${item?.taskType === "TEXT" || item?.taskType === "IMAGE" ? "text-secondary" : "text-on-surface-variant/40"}`}>
                {item?.taskType === "TEXT" || item?.taskType === "IMAGE" ? "Required" : "Optional"}
              </span>
            </label>
            <textarea 
              rows={4}
              placeholder={item?.taskType === "TEXT" || item?.taskType === "IMAGE" ? "Detail the execution steps taken (Required)..." : "Additional completion notes (Optional)..."}
              className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium resize-none text-on-surface"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
}
